'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { useTranslation } from '@/i18n/LanguageProvider';
import { Lightbulb, Send, Loader2, CheckCircle } from 'lucide-react';

export default function SuggestionsPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      // Buzon solo para usuarios registrados (no invitados anonimos).
      if (!u || u.isAnonymous) {
        router.push('/login?redirect=/sugerencias');
        return;
      }
      setUser(u);
      setCheckingAuth(false);
    });
    return () => unsub();
  }, [router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !title.trim() || description.trim().length < 10) return;
    setStatus('sending');
    try {
      await addDoc(collection(db, 'suggestions'), {
        userId: user.uid,
        userEmail: user.email || '',
        userName: user.displayName || '',
        title: title.trim().slice(0, 199),
        description: description.trim().slice(0, 2999),
        status: 'new',
        platform: 'web',
        createdAt: serverTimestamp(),
      });
      setStatus('sent');
      setTitle('');
      setDescription('');
    } catch (err) {
      console.error('[sugerencias] submit failed:', err);
      setStatus('error');
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-2">
          <Lightbulb className="w-7 h-7 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('suggestions.title')}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mb-8">{t('suggestions.intro')}</p>

        {status === 'sent' ? (
          <div className="bg-green-50 dark:bg-gray-800 border border-green-200 rounded-xl p-6 flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-900">
                {t('suggestions.sent')}
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-3 text-sm text-primary font-medium hover:underline"
              >
                {t('suggestions.sendAnother')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suggestions.titleLabel')}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                required
                placeholder={t('suggestions.titleHint')}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suggestions.descLabel')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
                rows={6}
                required
                placeholder={t('suggestions.descHint')}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary resize-y dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>
            {status === 'error' && (
              <p className="text-sm text-red-600">{t('suggestions.error')}</p>
            )}
            <button
              type="submit"
              disabled={status === 'sending' || !title.trim() || description.trim().length < 10}
              className="w-full flex items-center justify-center gap-2 bg-primary text-white font-semibold rounded-lg py-3 disabled:opacity-50"
            >
              {status === 'sending' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              {t('suggestions.send')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
